<template>
  <div class="login-page">
    <aside class="login-page__brand">
      <div class="login-page__brand-inner">
        <img
          class="login-page__logo"
          src="@/assets/images/common/logo.png"
          alt="知识图谱"
          @click="router.push('/search')"
        />
        <h1 class="login-page__headline">知识图谱系统</h1>
        <p class="login-page__tagline">连接实体与关系，探索结构化知识网络</p>
        <ul class="login-page__features">
          <li>多图谱管理与可视化浏览</li>
          <li>实体检索与关系脉络分析</li>
          <li>管理员后台统一维护</li>
        </ul>
      </div>
      <div class="login-page__brand-decoration" aria-hidden="true" />
    </aside>

    <main class="login-page__main">
      <img
        class="login-page__logo login-page__logo--mobile"
        src="@/assets/images/common/logo.png"
        alt="知识图谱"
        @click="router.push('/search')"
      />

      <div class="login-card">
        <header class="login-card__header">
          <h2 class="login-card__title">欢迎登录</h2>
          <p class="login-card__subtitle">使用锐研账号进入系统</p>
        </header>

        <el-form
          ref="loginFormRef"
          :model="loginForm"
          :rules="loginFormRules"
          class="login-card__form"
          size="large"
          @submit.prevent="submitForm"
        >
          <el-form-item prop="loginName">
            <el-input
              v-model="loginForm.loginName"
              placeholder="邮箱 / 手机号"
              type="text"
              autocomplete="username"
            >
              <template #prefix>
                <el-icon><User /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              v-model="loginForm.password"
              placeholder="密码"
              type="password"
              show-password
              autocomplete="current-password"
              @keyup.enter="submitForm"
            >
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <div class="login-card__options">
            <el-checkbox v-model="state.rememberMe">记住账号</el-checkbox>
          </div>
          <el-form-item class="login-card__submit-item">
            <el-button
              class="login-card__submit"
              :disabled="state.isLoginBtnDisabled"
              :loading="state.isLoginbtnLoading"
              type="primary"
              native-type="submit"
              @click="submitForm"
            >
              {{ state.loginBtnName }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </main>
  </div>
</template>

<script setup>
import CryptoJS from "crypto-js";
import store from "@/store";
import { useRoute, useRouter } from "vue-router";
import { reactive, ref, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { Lock, User } from "@element-plus/icons-vue";
import { isSsoLoginStrategy } from "@/core/loginStrategy";
import { goLogin } from "@/core/navigation";

const router = useRouter();
const route = useRoute();
const loginFormRef = ref(null);

const state = reactive({
  isLoginBtnDisabled: false,
  loginBtnName: "登录",
  isLoginbtnLoading: false,
  rememberMe: false,
});

const loginForm = reactive({
  loginName: "",
  password: "",
});

const loginFormRules = {
  loginName: [
    {
      required: true,
      message: "请输入手机号/用户名/邮箱",
      trigger: "blur",
    },
  ],
  password: [
    {
      required: true,
      message: "请输入密码",
      trigger: "blur",
    },
  ],
};

function md5Password(password) {
  if (/^[a-fA-F0-9]{32}$/.test(password)) {
    return password;
  }
  return CryptoJS.MD5(password).toString();
}

function resolveRedirect() {
  const redirect = route.query.redirect;
  if (typeof redirect === "string" && redirect.startsWith("/")) {
    return redirect;
  }
  return "/search";
}

async function submitForm() {
  if (!loginFormRef.value) {
    return;
  }
  const valid = await loginFormRef.value.validate().catch(() => false);
  if (!valid) {
    return;
  }

  state.isLoginBtnDisabled = true;
  state.isLoginbtnLoading = true;
  state.loginBtnName = "登录中";

  const passwordMd5 = md5Password(loginForm.password);

  try {
    const res = await store.dispatch("auth/loginWithOAuth", {
      loginName: loginForm.loginName,
      password: passwordMd5,
    });

    if (res && res.code === 0) {
      if (state.rememberMe) {
        window.localStorage.setItem("loginName", loginForm.loginName);
        window.localStorage.setItem("userPassword", passwordMd5);
      } else {
        window.localStorage.removeItem("loginName");
        window.localStorage.removeItem("userPassword");
      }
      router.replace(resolveRedirect());
      return;
    }

    if (res && res.message) {
      ElMessage.warning(res.message);
    }
  } finally {
    state.isLoginBtnDisabled = false;
    state.isLoginbtnLoading = false;
    state.loginBtnName = "登录";
  }
}

onMounted(() => {
  if (isSsoLoginStrategy()) {
    goLogin(resolveRedirect());
    return;
  }

  loginForm.loginName = window.localStorage.getItem("loginName") || "";
  const savedPassword = window.localStorage.getItem("userPassword");
  if (loginForm.loginName && savedPassword) {
    loginForm.password = savedPassword;
    state.rememberMe = true;
  }
});
</script>

<style lang="scss" scoped>
.login-page {
  --login-brand-from: #1a4fd6;
  --login-brand-to: #0d2a6b;
  --login-accent: #3b7cff;
  --login-text-muted: #6b7280;
  --login-card-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);

  display: flex;
  min-height: 100vh;
  background: #f4f7fc;

  &__brand {
    position: relative;
    flex: 1;
    max-width: 52%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
    background: linear-gradient(
      145deg,
      var(--login-brand-from) 0%,
      var(--login-brand-to) 100%
    );
    color: #fff;
    overflow: hidden;
  }

  &__brand-inner {
    position: relative;
    z-index: 1;
    max-width: 420px;
  }

  &__brand-decoration {
    position: absolute;
    inset: 0;
    background: radial-gradient(
        circle at 20% 80%,
        rgba(255, 255, 255, 0.12) 0%,
        transparent 45%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(59, 124, 255, 0.35) 0%,
        transparent 50%
      );
    pointer-events: none;
  }

  &__logo {
    height: 36px;
    margin-bottom: 32px;
    cursor: pointer;
    filter: brightness(0) invert(1);
  }

  &__logo--mobile {
    display: none;
    filter: none;
    margin-bottom: 24px;
  }

  &__headline {
    margin: 0 0 12px;
    font-size: 32px;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1.25;
  }

  &__tagline {
    margin: 0 0 28px;
    font-size: 15px;
    line-height: 1.6;
    opacity: 0.88;
  }

  &__features {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 18px;
      margin-bottom: 10px;
      font-size: 14px;
      line-height: 1.5;
      opacity: 0.9;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 8px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.85);
      }
    }
  }

  &__main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
  }
}

.login-card {
  width: 100%;
  max-width: 400px;
  padding: 40px 36px 32px;
  background: #fff;
  border-radius: 16px;
  box-shadow: var(--login-card-shadow);
  border: 1px solid rgba(15, 23, 42, 0.06);

  &__header {
    margin-bottom: 28px;
    text-align: center;
  }

  &__title {
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 700;
    color: #111827;
  }

  &__subtitle {
    margin: 0;
    font-size: 14px;
    color: var(--login-text-muted);
  }

  &__form {
    :deep(.el-input__wrapper) {
      border-radius: 10px;
      box-shadow: 0 0 0 1px #e5e7eb inset;
      padding: 4px 12px;

      &:hover {
        box-shadow: 0 0 0 1px #c7d2fe inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px var(--login-accent) inset;
      }
    }

    :deep(.el-input__prefix) {
      color: #9ca3af;
    }
  }

  &__options {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__submit-item {
    margin-bottom: 0;
    margin-top: 8px;
  }

  &__submit {
    width: 100%;
    height: 44px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.04em;
  }
}

@media (max-width: 768px) {
  .login-page {
    &__brand {
      display: none;
    }

    &__logo--mobile {
      display: block;
      align-self: flex-start;
    }

    &__main {
      justify-content: flex-start;
      padding-top: 48px;
    }
  }

  .login-card {
    padding: 28px 24px 24px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  }
}
</style>
